import * as React from "react";
import { useState, useMemo, useContext } from "react";
import styled from "styled-components";
import moment from "moment";
import MetricsSearchView from "security-app/components/common/MetricsSearchView";
import investigationsDashboardJson from "security-app/components/Investigations/dashboards/investigations-dashboard.json";
import useSearchVersionCheck from "hooks/useSearchVersionCheck";
import useSearchConfiguration from "hooks/useSearchConfiguration";
import { Spinner } from "components/common";
import { Alert } from "components/bootstrap";
import { widgetDragHandleClass, widgetActionsMenuClass } from "views/components/widgets/Constants";
const StyledSearchContainer = styled.div`
  footer,
  .query-tab-create,
  .query-config-btn,
  .fa-star,
  .react-resizable-handle,
  button:has(.fa-copy),
  button:has(.fa-chevron-down),
  .${widgetDragHandleClass}, .${widgetActionsMenuClass} {
    display: none;
  }

  .react-grid-layout,
  .container-fluid > .row:first-of-type {
    pointer-events: none;
  }
`;
const StyledVersionAlert = styled(Alert)`
  margin-left: 10px;
`;
const DashboardContext = React.createContext(null);
export function DashboardProvider({ children }) {
  const [refreshDasboard, setRefreshDasboard] = useState(false);
  const forceRefreshDashboard = () => {
    setRefreshDasboard(true);
    setTimeout(() => setRefreshDasboard(false), 800);
  };
  const value = useMemo(
    () => ({
      refreshDasboard,
      forceRefreshDashboard
    }),
    [refreshDasboard]
  );
  return /* @__PURE__ */ React.createElement(DashboardContext.Provider, { value }, children);
}
export const useDashboardContext = () => {
  const context = useContext(DashboardContext);
  return context || { forceRefreshDashboard: () => {
  } };
};
const DASHBOARD_TIME_RANGE_SECONDS = 7 * 24 * 60 * 60;
function Dashboard() {
  const { refreshDasboard: refreshDashboard } = useDashboardContext();
  const { config: searchConfig, isInitialLoading: searchConfigLoading } = useSearchConfiguration();
  const {
    data: usingMetricsCompatibleOpenSearch,
    isLoading: openSearchVersionLoading,
    error: openSearchVersionError
  } = useSearchVersionCheck("opensearch", "^2.0.0");
  const {
    data: usingMetricsCompatibleDataNode,
    isLoading: dataNodeSearchVersionLoading,
    error: dataNodeSearchVersionError
  } = useSearchVersionCheck("datanode", "^5.0.0");
  if (openSearchVersionLoading || dataNodeSearchVersionLoading || searchConfigLoading || refreshDashboard)
    return /* @__PURE__ */ React.createElement(Spinner, null);
  if (openSearchVersionError) return /* @__PURE__ */ React.createElement("div", null, "Error: ", openSearchVersionError.message);
  if (dataNodeSearchVersionError) return /* @__PURE__ */ React.createElement("div", null, "Error: ", dataNodeSearchVersionError.message);
  const queryTimeRangeLimit = moment.duration(searchConfig == null ? void 0 : searchConfig.query_time_range_limit).asSeconds();
  const isTimeRangeLimitTooLow = queryTimeRangeLimit > 0 && queryTimeRangeLimit < DASHBOARD_TIME_RANGE_SECONDS;
  if (isTimeRangeLimitTooLow) {
    return /* @__PURE__ */ React.createElement(StyledVersionAlert, { bsStyle: "warning", className: "tm", title: "Investigations metrics unavailable" }, /* @__PURE__ */ React.createElement("p", null, "Investigation metrics require a query time range of at least 7 days, but the configured query time range limit is ", moment.duration(searchConfig.query_time_range_limit).humanize(), ". Please increase the", " ", /* @__PURE__ */ React.createElement("b", null, "Query Time Range Limit"), " in the ", /* @__PURE__ */ React.createElement("em", null, "Search"), " configuration to at least 7 days, or disable it."));
  }
  return usingMetricsCompatibleOpenSearch.satisfied || usingMetricsCompatibleDataNode.satisfied ? /* @__PURE__ */ React.createElement(StyledSearchContainer, null, /* @__PURE__ */ React.createElement(MetricsSearchView, { viewJson: investigationsDashboardJson })) : /* @__PURE__ */ React.createElement(StyledVersionAlert, { bsStyle: "warning", className: "tm", title: "Investigations metrics disabled" }, /* @__PURE__ */ React.createElement("p", null, "Investigation metrics require OpenSearch 2.0.0 or Graylog Data Node 5.0.0 or greater. Please upgrade to collect and view the metrics."));
}
export default Dashboard;
